import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, CheckCircle2, XCircle, AlertCircle, Info, Minus } from 'lucide-react'

// ── LHR type subset ──────────────────────────────────────────────────────────

interface LhrAudit {
  id: string
  title: string
  description?: string
  score: number | null
  displayValue?: string
  scoreDisplayMode: 'numeric' | 'binary' | 'informative' | 'notApplicable' | 'manual' | 'error'
}

interface LhrAuditRef {
  id: string
  weight?: number
  group?: string
}

interface LhrCategory {
  id: string
  title: string
  score: number | null
  auditRefs: LhrAuditRef[]
}

interface LhrRaw {
  categories?: Record<string, LhrCategory>
  audits?: Record<string, LhrAudit>
}

// ── Helpers ──────────────────────────────────────────────────────────────────

type AuditResult = 'pass' | 'average' | 'fail' | 'info' | 'na'

function getAuditResult(audit: LhrAudit): AuditResult {
  if (audit.scoreDisplayMode === 'notApplicable') return 'na'
  if (audit.scoreDisplayMode === 'informative' || audit.scoreDisplayMode === 'manual') return 'info'
  if (audit.score === null) return 'info'
  if (audit.score >= 0.9) return 'pass'
  if (audit.score >= 0.5) return 'average'
  return 'fail'
}

function scoreColor(result: AuditResult): string {
  switch (result) {
    case 'pass': return 'var(--score-good)'
    case 'average': return 'var(--score-average)'
    case 'fail': return 'var(--score-poor)'
    default: return 'var(--text-muted)'
  }
}

function AuditIcon({ result }: { result: AuditResult }) {
  const color = scoreColor(result)
  const size = 15
  switch (result) {
    case 'pass':    return <CheckCircle2  size={size} style={{ color, flexShrink: 0 }} />
    case 'average': return <AlertCircle   size={size} style={{ color, flexShrink: 0 }} />
    case 'fail':    return <XCircle       size={size} style={{ color, flexShrink: 0 }} />
    case 'na':      return <Minus         size={size} style={{ color, flexShrink: 0 }} />
    default:        return <Info          size={size} style={{ color, flexShrink: 0 }} />
  }
}

const CATEGORY_ORDER = ['performance', 'accessibility', 'best-practices', 'seo']

const CATEGORY_LABELS: Record<string, string> = {
  performance:      'Performance',
  accessibility:    'Accessibility',
  'best-practices': 'Best Practices',
  seo:              'SEO',
}

// ── Main component ────────────────────────────────────────────────────────────

interface CategoryAccordionProps {
  rawReport: Record<string, unknown>
}

export default function CategoryAccordion({ rawReport }: CategoryAccordionProps) {
  const lhr = rawReport as unknown as LhrRaw
  const [openId, setOpenId] = useState<string | null>(null)

  if (!lhr.categories || !lhr.audits) {
    return (
      <p className="text-center text-sm" style={{ color: 'var(--text-muted)' }}>
        Detailed audit data unavailable.
      </p>
    )
  }

  const categoryKeys = CATEGORY_ORDER.filter(k => lhr.categories![k])

  return (
    <div className="space-y-2">
      {categoryKeys.map(catKey => {
        const category = lhr.categories![catKey]
        const isOpen = openId === catKey
        const categoryScore = category.score !== null ? Math.round(category.score * 100) : null

        // Resolve audit refs to actual audit objects, filtering out N/A entries
        const audits = (category.auditRefs ?? [])
          .map(ref => lhr.audits![ref.id])
          .filter((a): a is LhrAudit => !!a && a.scoreDisplayMode !== 'notApplicable')
          // Sort: fail first, then average, then pass, then info
          .sort((a, b) => {
            const order: Record<AuditResult, number> = { fail: 0, average: 1, pass: 2, info: 3, na: 4 }
            return order[getAuditResult(a)] - order[getAuditResult(b)]
          })

        const failCount = audits.filter(a => getAuditResult(a) === 'fail').length
        const avgCount  = audits.filter(a => getAuditResult(a) === 'average').length

        const scoreStyle: React.CSSProperties = {
          color: categoryScore === null
            ? 'var(--text-muted)'
            : categoryScore >= 90
            ? 'var(--score-good)'
            : categoryScore >= 50
            ? 'var(--score-average)'
            : 'var(--score-poor)',
        }

        return (
          <div
            key={catKey}
            className="glass rounded-xl overflow-hidden"
            style={{ border: '1px solid var(--border-subtle)' }}
          >
            {/* Header row */}
            <button
              onClick={() => setOpenId(isOpen ? null : catKey)}
              className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors"
              style={{ background: isOpen ? 'rgba(99,102,241,0.06)' : 'transparent' }}
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-3">
                <span
                  className="text-base font-bold tabular-nums"
                  style={{ ...scoreStyle, minWidth: '2.5rem' }}
                >
                  {categoryScore ?? '—'}
                </span>
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {CATEGORY_LABELS[catKey] ?? category.title}
                </span>
                {(failCount > 0 || avgCount > 0) && (
                  <span className="flex items-center gap-1.5 text-xs ml-1">
                    {failCount > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: 'rgba(239,68,68,0.12)', color: 'var(--score-poor)' }}>
                        {failCount} failed
                      </span>
                    )}
                    {avgCount > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: 'rgba(245,158,11,0.12)', color: 'var(--score-average)' }}>
                        {avgCount} needs work
                      </span>
                    )}
                  </span>
                )}
              </div>
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{ color: 'var(--text-muted)' }}
              >
                <ChevronDown size={16} />
              </motion.div>
            </button>

            {/* Audit list */}
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  style={{ overflow: 'hidden' }}
                >
                  <div
                    className="divide-y divide-white/5"
                    style={{ borderTop: '1px solid var(--border-subtle)' }}
                  >
                    {audits.length === 0 && (
                      <p className="px-5 py-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                        No applicable audits.
                      </p>
                    )}
                    {audits.map(audit => {
                      const result = getAuditResult(audit)
                      return (
                        <AuditRow key={audit.id} audit={audit} result={result} />
                      )
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}

// ── Audit row with expandable description ─────────────────────────────────────

function AuditRow({ audit, result }: { audit: LhrAudit; result: AuditResult }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className="px-5 py-3 transition-colors"
      style={{ background: expanded ? 'rgba(255,255,255,0.02)' : 'transparent' }}
    >
      <button
        onClick={() => audit.description && setExpanded(e => !e)}
        className="w-full flex items-start gap-3 text-left"
        style={{ cursor: audit.description ? 'pointer' : 'default' }}
      >
        <span className="mt-0.5">
          <AuditIcon result={result} />
        </span>
        <span className="flex-1 text-xs leading-relaxed" style={{ color: 'var(--text-primary)' }}>
          {audit.title}
        </span>
        {audit.displayValue && (
          <span
            className="text-xs tabular-nums shrink-0 font-mono"
            style={{ color: scoreColor(result) }}
          >
            {audit.displayValue}
          </span>
        )}
      </button>

      <AnimatePresence initial={false}>
        {expanded && audit.description && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-2 text-xs leading-relaxed pl-6 overflow-hidden"
            style={{ color: 'var(--text-secondary)' }}
          >
            {/* Strip markdown links from description */}
            {audit.description.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
}
